<template>
  <div class="d-flex flex-column mt-3">
    <div class="d-flex flex-column mb-3">
      <span class="label d-block mb-0">Глобальні обмеження дій</span>
      <span class="text-disabled font-size-12">
        Ці обмеження застосовуються до
        <strong class="text-black">всіх типів та видів документів.</strong>
      </span>
    </div>

    <div
      v-for="item in globalRestrictions"
      :key="item.id"
      class="d-flex align-center justify-space-between py-3 restriction-row">
      <span>{{ item.name }}</span>
      <div class="d-flex align-center ga-3">
        <span
          :class="item.enabled ? 'text-error' : 'text-disabled'"
          class="font-size-12 font-weight-bold text-uppercase">
          {{ item.enabled ? 'Заборонено' : 'Дозволено' }}
        </span>
        <v-switch
          v-model="item.enabled"
          color="error"
          density="comfortable"
          hide-details />
      </div>
    </div>

    <v-divider class="my-5"></v-divider>

    <div class="d-flex align-start justify-space-between ga-4 mb-4">
      <div class="d-flex flex-column">
        <span class="label d-block mb-0">Обмеження за видом документа</span>
        <span class="text-disabled font-size-12">
          Якщо глобальне обмеження вимкнено — заборону можна застосувати
          <strong class="text-black">лише до обраних видів</strong>. <br />
          Оберіть тип документа та позначте, які дії мають бути заборонені.
        </span>
      </div>
      <v-btn @click="createRestriction">
        <v-icon class="mr-1">mdi-plus</v-icon>
        Додати вид
      </v-btn>
    </div>

    <div class="text-disabled font-size-12 mb-3">
      {{ restrictions.length }} запис(ів) · {{ restrictionsCount }} обмежень
      встановлено
    </div>

    <div v-if="restrictions.length" class="d-flex flex-column ga-3">
      <v-card
        v-for="restriction in restrictions"
        :key="restriction.localId"
        border
        rounded="lg">
        <v-card-text class="pa-4">
          <div class="d-flex align-start justify-space-between ga-3">
            <div class="d-flex align-start ga-3">
              <v-avatar color="blue-grey-lighten-5" rounded="lg" size="36">
                <v-icon color="grey-darken-1">mdi-lock-outline</v-icon>
              </v-avatar>
              <div class="d-flex flex-column">
                <span class="font-weight-bold">
                  {{ getDocTypeName(restriction.docTypeDefinitionId) }}
                </span>
                <span class="text-disabled font-size-13">
                  <v-chip
                    class="mr-2"
                    color="success"
                    density="comfortable"
                    size="small"
                    variant="tonal">
                    {{ getDocTypeClassName(restriction.docTypeClassId) }}
                  </v-chip>
                  · {{ restriction.restrictionTypeIds.length }} обмеження
                </span>
              </div>
            </div>

            <div class="d-flex align-center ga-2">
              <v-btn
                border
                class="font-size-12"
                color="white"
                variant="flat"
                @click="editRestriction(restriction)">
                Редагувати
              </v-btn>
              <v-btn
                border
                color="white"
                density="comfortable"
                icon
                variant="flat"
                @click="deleteRestriction(restriction.localId)">
                <v-icon color="error">mdi-delete-outline</v-icon>
              </v-btn>
            </div>
          </div>

          <v-divider class="my-3" variant="dashed"></v-divider>

          <div class="d-flex flex-wrap ga-2">
            <v-tooltip
              v-for="typeId in restriction.restrictionTypeIds"
              :key="typeId"
              :disabled="!globallyRestrictedTypeIds.includes(typeId)"
              location="top">
              <template #activator="{ props: tooltipProps }">
                <v-chip
                  v-bind="tooltipProps"
                  :color="getRestrictionColor(typeId)"
                  :opacity="
                    globallyRestrictedTypeIds.includes(typeId) ? '0.5' : '1'
                  "
                  border
                  size="small"
                  variant="tonal">
                  <v-icon class="mr-1" size="small">
                    {{
                      globallyRestrictedTypeIds.includes(typeId)
                        ? 'mdi-earth'
                        : 'mdi-cancel'
                    }}
                  </v-icon>
                  <span class="font-weight-bold">
                    {{ getRestrictionShortName(typeId) }}
                  </span>
                </v-chip>
              </template>
              <span>Вже заборонено глобально</span>
            </v-tooltip>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <v-card v-else border class="bg-grey-lighten-5" rounded="lg">
      <v-card-text class="text-center text-disabled py-6">
        Обмеження за видами документів ще не додані
      </v-card-text>
    </v-card>

    <div class="d-flex justify-end ga-2 mt-5">
      <v-btn
        color="white"
        rounded="lg"
        style="border: 1px solid #e4e4e7"
        variant="flat"
        @click="dialog.close()">
        Скасувати
      </v-btn>
      <v-btn rounded="lg" @click="saveSettings">
        <v-icon class="mr-1" size="small">mdi-check</v-icon>
        Зберегти параметри
      </v-btn>
    </div>
  </div>
</template>

<script lang="ts">
import { openDialog } from '@/dialog'
import { computed, onMounted, ref } from 'vue'
import { setSnackbar } from 'best-modules/plugins'
import { fillDirectory, getDirectory } from '@/directory'
import { cloneDeep, uniq } from 'lodash'
import {
  userDelegationUpdateRestrictions,
  userDelegationSingle,
} from '@/requests/user'

type RestrictionCard = {
  id?: number | string
  localId: number | string
  docTypeClassId: number | null
  docTypeDefinitionId: number | null
  restrictionTypeIds: number[]
}

export default {
  name: 'DelegationSettings',
  props: {
    dialog: { type: Object, required: true },
  },
  setup(props) {
    const existingGlobalTypeIds = getGlobalTypeIds(
      props.dialog.dialogData.delegation.restrictions
    )

    const globalRestrictions = ref([
      {
        id: 1,
        name: 'Заборона погодження документів',
        enabled: existingGlobalTypeIds.includes(1),
      },
      {
        id: 2,
        name: 'Заборона підписання документів',
        enabled: existingGlobalTypeIds.includes(2),
      },
    ])

    const restrictions = ref<RestrictionCard[]>(
      normalizeRestrictions(props.dialog.dialogData.delegation.restrictions)
    )

    const restrictionsCount = computed(() => {
      return restrictions.value.reduce((sum, item) => {
        return sum + item.restrictionTypeIds.length
      }, 0)
    })

    const globallyRestrictedTypeIds = computed(() =>
      globalRestrictions.value.filter(g => g.enabled).map(g => g.id)
    )

    const createRestriction = () => {
      return openDialog({
        name: 'DelegationRestriction',
        action: 'create',
        dialogData: {
          existingRestrictions: restrictions.value,
          globallyRestrictedTypeIds: globallyRestrictedTypeIds.value,
        },
        params: {
          submitLabel: 'Додати до списку',
          cb: restriction => {
            restrictions.value.push({
              ...restriction,
              localId: Date.now(),
            })
            setSnackbar({ text: 'Обмеження додано до списку' })
          },
        },
      })
    }

    const editRestriction = (restriction: RestrictionCard) => {
      return openDialog({
        name: 'DelegationRestriction',
        action: 'update',
        dialogData: {
          restriction: cloneDeep(restriction),
          existingRestrictions: restrictions.value.filter(
            item => item.localId !== restriction.localId
          ),
          globallyRestrictedTypeIds: globallyRestrictedTypeIds.value,
        },
        params: {
          submitLabel: 'Зберегти',
          cb: updatedRestriction => {
            restrictions.value = restrictions.value.map(item => {
              if (item.localId === restriction.localId) {
                return {
                  ...updatedRestriction,
                  localId: restriction.localId,
                }
              }

              return item
            })

            setSnackbar({ text: 'Обмеження оновлено' })
          },
        },
      })
    }

    const deleteRestriction = (localId: number | string) => {
      restrictions.value = restrictions.value.filter(
        restriction => restriction.localId !== localId
      )
    }

    const saveSettings = async () => {
      const enabledGlobalTypeIds = globalRestrictions.value
        .filter(g => g.enabled)
        .map(g => g.id)

      const apiRestrictions: {
        docTypeDefinitionId: number | null
        restrictionTypeIds: number[]
      }[] = []

      if (enabledGlobalTypeIds.length > 0) {
        apiRestrictions.push({
          docTypeDefinitionId: null,
          restrictionTypeIds: enabledGlobalTypeIds,
        })
      }

      restrictions.value.forEach(r => {
        apiRestrictions.push({
          docTypeDefinitionId: r.docTypeDefinitionId,
          restrictionTypeIds: r.restrictionTypeIds,
        })
      })

      await userDelegationUpdateRestrictions(
        props.dialog.dialogData.delegationId,
        apiRestrictions
      )

      const updatedDelegation = await userDelegationSingle(
        props.dialog.dialogData.delegationId
      )
      props.dialog.dialogData.setDelegation(updatedDelegation)
      setSnackbar({ text: 'Параметри делегування збережено' })
      props.dialog.close()
    }

    const getDocTypeClassName = (id: number | null) => {
      return (
        getDirectory('docTypeClasses').find(item => item.id === id)?.name || '—'
      )
    }

    const getDocTypeName = (id: number | null) => {
      return (
        getDirectory('docTypeDefinitions').find(item => item.id === id)?.name ||
        '—'
      )
    }

    const getRestrictionName = (id: number) => {
      return (
        getDirectory('delegationRestrictionTypes').find(item => item.id === id)
          ?.name || 'Обмеження'
      )
    }

    const getRestrictionShortName = (id: number) => {
      return getRestrictionName(id)
        .replace('Заборона ', '')
        .replace(' документів', '')
    }

    const getRestrictionColor = (id: number) => {
      return (
        {
          1: 'blue',
          2: 'green',
          3: 'orange',
          4: 'red',
          5: 'purple',
        }[id] || 'grey'
      )
    }

    onMounted(() => {
      fillDirectory([
        'docTypeClasses',
        'docTypeDefinitions',
        'delegationRestrictionTypes',
      ])
    })

    return {
      globalRestrictions,
      restrictions,
      restrictionsCount,
      globallyRestrictedTypeIds,
      createRestriction,
      editRestriction,
      deleteRestriction,
      saveSettings,
      getDocTypeClassName,
      getDocTypeName,
      getRestrictionShortName,
      getRestrictionColor,
    }
  },
}

function getGlobalTypeIds(source: any[] = []): number[] {
  if (!source?.length) return []

  if (source[0]?.restrictionTypeIds) {
    const globalCard = source.find(item => item.docTypeDefinitionId === null)
    return globalCard?.restrictionTypeIds || []
  }

  return source
    .filter(item => !item.docTypeDefinitionId)
    .map(item =>
      Number(item.restrictionTypeId?.value ?? item.restrictionTypeId)
    )
}

function normalizeRestrictions(source: any[] = []): RestrictionCard[] {
  if (!source?.length) {
    return []
  }

  if (source[0]?.restrictionTypeIds) {
    return source
      .filter(item => item.docTypeDefinitionId !== null)
      .map((item, index) => ({
        ...item,
        localId: item.localId || item.id || `restriction-${index}`,
        restrictionTypeIds: item.restrictionTypeIds || [],
      }))
  }

  const grouped = source.reduce((acc, item) => {
    const docTypeDefinitionId = item.docTypeDefinitionId || null
    if (docTypeDefinitionId === null) return acc

    const key = String(docTypeDefinitionId)

    if (!acc[key]) {
      acc[key] = {
        id: item.id,
        localId: item.id || key,
        docTypeClassId:
          item.docTypeClassId ||
          item.docTypeDefinition?.docTypeClassId ||
          item.docTypeDefinition?.doc_type_class_id ||
          null,
        docTypeDefinitionId,
        restrictionTypeIds: [],
      }
    }

    const typeId = Number(
      item.restrictionTypeId?.value ?? item.restrictionTypeId
    )
    acc[key].restrictionTypeIds.push(typeId)
    acc[key].restrictionTypeIds = uniq(acc[key].restrictionTypeIds)

    return acc
  }, {})

  return Object.values(grouped) as RestrictionCard[]
}
</script>

<style lang="scss" scoped>
.restriction-row {
  border-bottom: 1px solid #f5f5f8;
}
</style>
